import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../context/Session';
import { DAY_KEYS, useT } from '../i18n';
import { Empty, ExerciseThumb, Icon, ImagePreview, Loading, Menu, Modal, Spinner, Switch, useConfirm, useToast } from '../components/ui';
import { useCollection, useExercises } from '../lib/hooks';
import { appendEntries, countExercises, customExerciseFor, defaultEntryFor, estimateDayMinutes, foldForSearch, formatWeight, isCustomExercise, newCustomExercise, parseProgram, resolveExercise, sortDays, withExercise } from '../lib/program';
import { useProgram } from '../lib/useUserData';
import { createProgram, deleteProgramSnapshot, historyQuery, maxSnapshotsFor, restoreProgramSnapshot, insightsQuery, markStarterTodoDone, saveInsight, saveProgram, saveProgramSnapshot, updateUser } from '../lib/services';
import { AI_KIND, allowanceBlockedMessage, getAiAllowance, recordAiAllowanceUse } from '../lib/allowance';
import { analyzeProgram } from '../lib/cloud';
import { formatDate, isoWeekday, tsToDate } from '../lib/dates';
import { exerciseMetaLine } from './Home';

const clone = (p) => JSON.parse(JSON.stringify(p));
const todayDayKey = () => DAY_KEYS[isoWeekday(new Date()) - 1];

/* ───────── Edit sets / reps / duration / weight ───────── */
const PRESETS = [[3, 8], [3, 10], [3, 12], [4, 10], [5, 5]];

/** [-] value [+]; holding a button repeats the step. Empty value = off when `zeroLabel` is set. */
function Stepper({ label, value, onChange, min = 0, max = 999, step = 1, decimal = false, zeroLabel }) {
  const timer = useRef(null);
  const latest = useRef(value);
  latest.current = value;
  const num = (v) => {
    const n = parseFloat(String(v).replace(',', '.'));
    return Number.isNaN(n) ? 0 : n;
  };
  const bump = (dir) => {
    let next = num(latest.current) + dir * step;
    if (decimal) next = Math.round(next / step) * step;
    next = Math.min(max, Math.max(min, next));
    const text = next <= 0 && zeroLabel ? '' : String(decimal ? Math.round(next * 10) / 10 : Math.round(next));
    latest.current = text;
    onChange(text);
  };
  const stop = () => {
    clearTimeout(timer.current);
    clearInterval(timer.current);
    timer.current = null;
  };
  const start = (dir) => {
    bump(dir);
    stop();
    timer.current = setTimeout(() => { timer.current = setInterval(() => bump(dir), 90); }, 400);
  };
  useEffect(() => stop, []);
  const hold = (dir) => ({
    onPointerDown: (e) => { e.preventDefault(); start(dir); },
    onPointerUp: stop,
    onPointerLeave: stop,
    onPointerCancel: stop,
    onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); bump(dir); } },
  });
  return (
    <div className="gm-stepper-row">
      <span className="gm-grow">{label}</span>
      <div className="gm-stepper">
        <button type="button" aria-label={`${label} −`} disabled={num(value) <= min} {...hold(-1)}><Icon name="remove" size={18} /></button>
        <input
          className="gm-input"
          inputMode={decimal ? 'decimal' : 'numeric'}
          placeholder={zeroLabel}
          value={value}
          onChange={(e) => onChange(e.target.value.replace(decimal ? /[^0-9.,]/g : /[^0-9]/g, ''))}
        />
        <button type="button" aria-label={`${label} +`} disabled={num(value) >= max} {...hold(1)}><Icon name="add" size={18} /></button>
      </div>
    </div>
  );
}

function EditEntryModal({ entry, exercise, appearsOnOtherDays, applyToAllDefault, onClose, onSave }) {
  const t = useT();
  const [sets, setSets] = useState(String(entry.sets));
  const [reps, setReps] = useState(String(entry.reps));
  const [duration, setDuration] = useState(entry.durationMin ? String(entry.durationMin) : '');
  const [weight, setWeight] = useState(entry.weight != null ? formatWeight(entry.weight) : '');
  const [applyAll, setApplyAll] = useState(!!applyToAllDefault);
  const save = () => {
    const w = parseFloat(String(weight).replace(',', '.'));
    const d = parseInt(duration, 10);
    onSave({
      ...entry,
      sets: Math.min(99, Math.max(1, parseInt(sets, 10) || 1)),
      reps: Math.min(999, Math.max(1, parseInt(reps, 10) || 1)),
      durationMin: d > 0 ? Math.min(999, d) : null,
      weight: w > 0 ? Math.min(500, w) : null,
    }, appearsOnOtherDays && applyAll);
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={exercise?.name || entry.exerciseId}
      footer={(
        <>
          <button type="button" className="gm-btn gm-btn--ghost" onClick={onClose}>{t('cancel')}</button>
          <button type="button" className="gm-btn" onClick={save}>{t('save')}</button>
        </>
      )}
    >
      {exercise?.muscle && <p className="gm-muted gm-small" style={{ marginTop: -6, marginBottom: 14 }}>{t.muscle(exercise.muscle)}</p>}
      <div className="gm-upper-sm" style={{ marginBottom: 8 }}>{t.upper(t('programQuickPicks'))}</div>
      <div className="gm-chips" style={{ marginBottom: 14 }}>
        {PRESETS.map(([s, r]) => (
          <button
            key={`${s}x${r}`}
            type="button"
            className={`gm-chip${String(s) === String(sets) && String(r) === String(reps) ? ' selected' : ''}`}
            onClick={() => { setSets(String(s)); setReps(String(r)); }}
          >
            {s}×{r}
          </button>
        ))}
      </div>
      <Stepper label={t('sets')} value={sets} onChange={setSets} min={1} max={99} />
      <Stepper label={t('reps')} value={reps} onChange={setReps} min={1} max={999} />
      <Stepper label={t('programExerciseDurationLabel')} value={duration} onChange={setDuration} max={999} zeroLabel={t('programNotSet')} />
      <Stepper label={t('programExerciseWeightLabel')} value={weight} onChange={setWeight} max={500} step={2.5} decimal zeroLabel={t('programNotSet')} />
      {appearsOnOtherDays && (
        <div className="gm-row-between" style={{ marginTop: 12, gap: 12 }}>
          <span>{t('programApplyToAllDays')}</span>
          <Switch checked={applyAll} onChange={setApplyAll} label={t('programApplyToAllDays')} />
        </div>
      )}
    </Modal>
  );
}

/* ───────── Add / replace exercise ───────── */
/**
 * Exercise picker: search (English names, localized muscle/equipment labels and
 * Turkish aliases), muscle chips and multi-select. With `multi` false a click
 * picks at once (used for "replace exercise"). `extra` = custom exercises already
 * in the program; new custom exercises can be created here too. `onPick` gets
 * the picked exercise objects.
 */
function AddExerciseModal({ title, idsInDay, idsInProgram = [], extra = [], multi = true, onClose, onPick }) {
  const t = useT();
  const { list } = useExercises();
  const [q, setQ] = useState('');
  const [muscle, setMuscle] = useState('');
  const [selected, setSelected] = useState([]);
  const [created, setCreated] = useState([]);
  // Non-null while the "create custom exercise" form is shown.
  const [draftCustom, setDraftCustom] = useState(null);
  const muscles = Array.from(new Set(list.map((e) => e.muscle).filter(Boolean)))
    .sort((a, b) => t.muscle(a).localeCompare(t.muscle(b), t.locale));
  const sorted = [...list, ...extra, ...created].sort((a, b) => a.name.localeCompare(b.name));
  const s = foldForSearch(q.trim());
  const filtered = sorted.filter((e) => {
    if (muscle && e.muscle !== muscle) return false;
    if (!s) return true;
    return [e.name, e.muscle, t.muscle(e.muscle), e.equipment, t.equipment(e.equipment), ...(e.aliases_tr || [])]
      .some((x) => foldForSearch(x).includes(s)) || e.id.includes(s);
  });
  const inProgram = filtered.filter((e) => idsInProgram.includes(e.id) && !idsInDay.includes(e.id));

  const toggle = (e) => {
    if (!multi) {
      onPick([e]);
      return;
    }
    setSelected((cur) => (cur.some((x) => x.id === e.id) ? cur.filter((x) => x.id !== e.id) : [...cur, e]));
  };

  const saveCustom = () => {
    const name = draftCustom.name.trim();
    if (!name) return;
    const ex = newCustomExercise(name, draftCustom.muscle);
    if (!multi) {
      onPick([ex]);
      return;
    }
    setCreated((cur) => [...cur, ex]);
    setSelected((cur) => [...cur, ex]);
    setDraftCustom(null);
    setQ('');
    setMuscle('');
  };

  if (draftCustom) {
    return (
      <Modal
        open
        onClose={() => setDraftCustom(null)}
        title={t('programCreateCustomExercise')}
        footer={(
          <>
            <button type="button" className="gm-btn gm-btn--ghost" onClick={() => setDraftCustom(null)}>{t('back')}</button>
            <button type="button" className="gm-btn" disabled={!draftCustom.name.trim()} onClick={saveCustom}>{t('save')}</button>
          </>
        )}
      >
        <form className="gm-stack" onSubmit={(ev) => { ev.preventDefault(); saveCustom(); }}>
          <div className="gm-field">
            <label htmlFor="custom-ex-name">{t('programCustomExerciseName')}</label>
            <input
              id="custom-ex-name"
              className="gm-input"
              maxLength={60}
              autoFocus
              autoComplete="off"
              value={draftCustom.name}
              onChange={(ev) => setDraftCustom({ ...draftCustom, name: ev.target.value })}
            />
          </div>
          <div className="gm-field">
            <label htmlFor="custom-ex-muscle">{t('programCustomExerciseMuscle')}</label>
            <select
              id="custom-ex-muscle"
              className="gm-select"
              value={draftCustom.muscle}
              onChange={(ev) => setDraftCustom({ ...draftCustom, muscle: ev.target.value })}
            >
              <option value="">{t('programCustomExerciseNoMuscle')}</option>
              {muscles.map((m) => <option key={m} value={m}>{t.muscle(m)}</option>)}
            </select>
          </div>
        </form>
      </Modal>
    );
  }

  const item = (e, keyPrefix) => {
    const added = idsInDay.includes(e.id);
    const on = selected.some((x) => x.id === e.id);
    const meta = [isCustomExercise(e) && t('programCustomExerciseBadge'), t.muscle(e.muscle), t.equipment(e.equipment)].filter(Boolean).join(' · ');
    return (
      <div key={`${keyPrefix}${e.id}`} className={`gm-picker-item${added ? ' added' : ''}${on ? ' selected' : ''}`} onClick={() => !added && toggle(e)}>
        <ExerciseThumb exercise={e} />
        <div className="gm-grow">
          <div className="gm-ex-name">{e.name}</div>
          <div className="gm-ex-meta">{added ? t('programAlreadyInDay') : meta}</div>
        </div>
        {added && <Icon name="check_circle" className="gm-muted" />}
        {!added && multi && <Icon name={on ? 'check_box' : 'check_box_outline_blank'} style={{ color: on ? 'var(--gm-primary)' : 'var(--gm-muted)' }} />}
      </div>
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      wide
      footer={multi ? (
        <>
          <button type="button" className="gm-btn gm-btn--ghost" onClick={onClose}>{t('cancel')}</button>
          <button type="button" className="gm-btn" disabled={!selected.length} onClick={() => onPick(selected)}>
            <Icon name="add" /> {selected.length ? t('programAddExercisesCta', selected.length) : t('add')}
          </button>
        </>
      ) : undefined}
    >
      <div className="gm-search">
        <Icon name="search" />
        <input className="gm-input gm-input--search" placeholder={t('searchExercises')} value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
      </div>
      <div className="gm-chips gm-chips--scroll" style={{ marginTop: 12 }}>
        <button type="button" className={`gm-chip${!muscle ? ' selected' : ''}`} onClick={() => setMuscle('')}>{t('programFilterAll')}</button>
        {muscles.map((m) => (
          <button key={m} type="button" className={`gm-chip${muscle === m ? ' selected' : ''}`} onClick={() => setMuscle(m)}>{t.muscle(m)}</button>
        ))}
      </div>
      <div className="gm-picker-list">
        <div className="gm-picker-item" role="button" tabIndex={0} onClick={() => setDraftCustom({ name: q.trim(), muscle: '' })} onKeyDown={(ev) => { if (ev.key === 'Enter') setDraftCustom({ name: q.trim(), muscle: '' }); }}>
          <div className="gm-ex-thumb" style={{ color: 'var(--gm-primary)' }}><Icon name="edit_note" /></div>
          <div className="gm-grow">
            <div className="gm-ex-name" style={{ color: 'var(--gm-primary)' }}>
              {q.trim() ? t('programAddCustomExerciseNamed', q.trim()) : t('programCreateCustomExercise')}
            </div>
          </div>
        </div>
        {filtered.length === 0 && <Empty icon="search_off">{t('noExercisesFound')}</Empty>}
        {inProgram.length > 0 && (
          <>
            <div className="gm-picker-head">{t('programInYourProgram')}</div>
            {inProgram.map((e) => item(e, 'p-'))}
            <div className="gm-picker-head">{t('programAllExercises')}</div>
          </>
        )}
        {filtered.map((e) => item(e, ''))}
      </div>
    </Modal>
  );
}

/* ───────── Day picker ───────── */
/** Pick one day (`multi` false) or several from all seven; days not in `selectable` are disabled. */
function DayPickerModal({ title, selectable, multi = false, disabledReason, onClose, onPick }) {
  const t = useT();
  const [picked, setPicked] = useState([]);
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={multi ? (
        <>
          <button type="button" className="gm-btn gm-btn--ghost" onClick={onClose}>{t('cancel')}</button>
          <button type="button" className="gm-btn" disabled={!picked.length} onClick={() => onPick(DAY_KEYS.filter((d) => picked.includes(d)))}>{t('save')}</button>
        </>
      ) : undefined}
    >
      <div className="gm-stack-sm">
        {DAY_KEYS.map((d) => {
          const enabled = selectable.includes(d);
          const on = picked.includes(d);
          return (
            <button
              key={d}
              type="button"
              className={`gm-chip${on ? ' selected' : ''}`}
              style={{ justifyContent: 'space-between', width: '100%', minHeight: 44 }}
              disabled={!enabled}
              onClick={() => (multi ? setPicked((cur) => (on ? cur.filter((x) => x !== d) : [...cur, d])) : onPick([d]))}
            >
              <span style={{ opacity: enabled ? 1 : 0.4 }}>{t(d)}</span>
              {!enabled && disabledReason && <span className="gm-tiny gm-muted">{disabledReason}</span>}
              {enabled && multi && <Icon name={on ? 'check_box' : 'check_box_outline_blank'} size={20} />}
              {enabled && !multi && <Icon name="chevron_right" size={20} />}
            </button>
          );
        })}
      </div>
    </Modal>
  );
}

/* ───────── Read-only program (snapshots, previews, trainer view) ───────── */
export function ProgramReadOnly({ program, durationFirst = true, titles }) {
  const t = useT();
  const { byId } = useExercises();
  const days = sortDays(Object.keys(program || {}));
  if (!days.length) return <Empty icon="event_busy">{t('noProgramData')}</Empty>;
  return (
    <div className="gm-stack">
      {days.map((d) => (
        <div key={d}>
          <div className="gm-upper-sm" style={{ marginBottom: 6 }}>
            {t.upper(t(d))}
            {titles?.[d] && <span className="gm-ait-day-title"> · {titles[d]}</span>}
          </div>
          <div className="gm-card" style={{ padding: '4px 14px' }}>
            {program[d].length === 0 && <div className="gm-muted gm-small" style={{ padding: '10px 0' }}>{t('restDay')}</div>}
            {program[d].map((e) => (
              <div key={e.exerciseId} className="gm-ex-row">
                <ExerciseThumb exercise={resolveExercise(byId, e)} />
                <div className="gm-grow">
                  <div className="gm-ex-name">{resolveExercise(byId, e)?.name || e.exerciseId}</div>
                  <div className="gm-ex-meta">{exerciseMetaLine(t, e, durationFirst)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ───────── AI insight ───────── */
function InsightSections({ result }) {
  const t = useT();
  const section = (title, list, icon) => (
    <div>
      <div className="gm-row" style={{ marginBottom: 6 }}><Icon name={icon} size={20} outline /><span className="gm-strong">{title}</span></div>
      <ul style={{ margin: 0, paddingLeft: 22 }} className="gm-stack-sm">
        {list.map((x) => <li key={x} style={{ lineHeight: 1.5 }}>{x}</li>)}
      </ul>
    </div>
  );
  return (
    <div className="gm-stack">
      {section(t('aiInsightPros'), result.pros, 'thumb_up')}
      {section(t('aiInsightCons'), result.cons, 'thumb_down')}
      {section(t('aiInsightAdvices'), result.advices, 'lightbulb')}
    </div>
  );
}

function InsightModal({ user, program, onClose }) {
  const t = useT();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [openId, setOpenId] = useState(null);
  const { docs } = useCollection(insightsQuery(user.id), `ins/${user.id}`);

  const run = async () => {
    const allowance = await getAiAllowance(user.id, AI_KIND.programAnalysis);
    if (!allowance.canUse) {
      toast.warning(allowanceBlockedMessage(t, allowance));
      return;
    }
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const r = await analyzeProgram(user.id);
      await saveInsight(user.id, user.exerciseProgramID, program, r).catch(() => {});
      setResult(r);
    } catch (e) {
      setError(t('aiInsightAnalysisError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={t('aiInsightProgramTitle')} wide>
      <button type="button" className="gm-btn gm-btn--block gm-btn--lg" onClick={run} disabled={busy}>
        {busy ? <Spinner /> : <Icon name="analytics" outline />}
        {busy ? t('aiInsightAnalysisLoading') : t('aiInsightProgramAnalysis')}
      </button>
      {error && <p className="gm-error-text" style={{ marginTop: 12 }}>{error}</p>}
      {result && <div style={{ marginTop: 20 }}><InsightSections result={result} /></div>}
      <div className="gm-upper-sm" style={{ margin: '26px 0 8px' }}>{t.upper(t('allInsights'))}</div>
      {docs.length === 0 ? (
        <p className="gm-muted gm-small">{t('noInsights')}</p>
      ) : (
        <div className="gm-card gm-card--flush">
          {docs.map((d) => (
            <div key={d.id}>
              <div className="gm-list-item" style={{ cursor: 'pointer' }} onClick={() => setOpenId(openId === d.id ? null : d.id)}>
                <Icon name="auto_awesome" style={{ color: 'var(--gm-insight)' }} />
                <div className="gm-grow">
                  <div className="gm-strong">{formatDate(tsToDate(d.createdAt), t.locale, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                  <div className="gm-tiny gm-muted">{(d.pros || []).length + (d.cons || []).length + (d.advices || []).length} •</div>
                </div>
                <Icon name={openId === d.id ? 'expand_less' : 'expand_more'} />
              </div>
              {openId === d.id && (
                <div style={{ padding: '4px 16px 16px' }}>
                  <InsightSections result={{ pros: d.pros || [], cons: d.cons || [], advices: d.advices || [] }} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ───────── Program history snapshots ───────── */
function SnapshotModal({ user, program, onClose }) {
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState(null);
  const { docs } = useCollection(historyQuery(user.id), `ph/${user.id}`);
  const atCap = docs.length >= maxSnapshotsFor(user.subscription);

  const remove = async (d) => {
    const ok = await confirm({ title: t('programHistoryDeleteTitle'), message: t('programHistoryDeleteConfirm'), confirmLabel: t('deleteEntry'), danger: true });
    if (!ok) return false;
    try {
      await deleteProgramSnapshot(user.id, d.id);
      toast.show(t('programHistoryDeleted'));
      return true;
    } catch (e) {
      toast.error(t('anErrorOccurred'));
      return false;
    }
  };

  const take = async () => {
    if (countExercises(program) === 0) {
      toast.warning(t('programHistorySnapshotNothingToSave'));
      return;
    }
    const allowance = await getAiAllowance(user.id, AI_KIND.programSnapshot);
    if (!allowance.canUse) {
      toast.warning(allowanceBlockedMessage(t, allowance));
      return;
    }
    if (atCap) {
      toast.warning(t('programHistoryLimitReached'), { duration: 5000 });
      return;
    }
    setBusy(true);
    try {
      await saveProgramSnapshot(user.id, program);
      await recordAiAllowanceUse(user.id, AI_KIND.programSnapshot);
      toast.success(t('programHistorySnapshotSaved'));
    } catch (e) {
      toast.error(t('programHistorySnapshotFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (view) {
    const snap = parseProgram(view.program_snapshot);
    const canRestore = !!user.exerciseProgramID && Object.keys(snap).length > 0;
    const restore = async () => {
      const ok = await confirm({
        title: t('programHistoryRestoreConfirmTitle'),
        message: t('programHistoryRestoreConfirmMessage'),
        confirmLabel: t('programHistoryRestore'),
      });
      if (!ok) return;
      const allowance = await getAiAllowance(user.id, AI_KIND.programSnapshot);
      if (!allowance.canUse) {
        toast.warning(allowanceBlockedMessage(t, allowance));
        return;
      }
      // The automatic backup of the current program needs room under the total cap.
      if (atCap && countExercises(program) > 0) {
        toast.warning(t('programHistoryLimitReached'), { duration: 5000 });
        return;
      }
      setBusy(true);
      try {
        await restoreProgramSnapshot(user.id, user.exerciseProgramID, program, snap);
        await recordAiAllowanceUse(user.id, AI_KIND.programSnapshot);
        toast.success(t('programHistoryRestored'));
        onClose();
      } catch (e) {
        toast.error(t('programHistoryRestoreFailed'));
      } finally {
        setBusy(false);
      }
    };
    return (
      <Modal
        open
        onClose={busy ? undefined : () => setView(null)}
        title={t('programHistorySnapshotTitle')}
        wide
        footer={(
          <>
            <button
              type="button"
              className="gm-btn gm-btn--ghost"
              style={{ color: 'var(--gm-error)', marginRight: 'auto' }}
              disabled={busy}
              onClick={async () => { if (await remove(view)) setView(null); }}
            >
              <Icon name="delete" outline /> {t('deleteEntry')}
            </button>
            <button type="button" className="gm-btn gm-btn--ghost" onClick={() => setView(null)} disabled={busy}>{t('back')}</button>
            {canRestore && (
              <button type="button" className="gm-btn" onClick={restore} disabled={busy}>
                {busy ? <Spinner /> : <Icon name="restore" />} {t('programHistoryRestore')}
              </button>
            )}
          </>
        )}
      >
        <p className="gm-muted gm-small" style={{ marginBottom: 14 }}>{formatDate(tsToDate(view.created_at), t.locale, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
        <ProgramReadOnly program={snap} durationFirst={user.programDurationInsteadOfSetsReps} />
      </Modal>
    );
  }

  return (
    <Modal open onClose={onClose} title={t('programHistorySheetTitle')} wide>
      <p className="gm-muted" style={{ marginBottom: 14 }}>{t('programHistorySheetSubtitle')}</p>
      <button type="button" className="gm-btn gm-btn--block gm-btn--lg" onClick={take} disabled={busy}>
        {busy ? <Spinner /> : <Icon name="photo_camera" outline />} {t('programHistoryTakeSnapshot')}
      </button>
      <div className="gm-upper-sm" style={{ margin: '26px 0 8px' }}>{t.upper(t('programHistoryAllSnapshots'))}</div>
      {docs.length === 0 ? (
        <p className="gm-muted gm-small">{t('programHistoryNoSnapshots')}</p>
      ) : (
        <div className="gm-card gm-card--flush">
          {docs.map((d) => {
            const p = parseProgram(d.program_snapshot);
            return (
              <div key={d.id} className="gm-list-item">
                <Icon name="history" />
                <div className="gm-grow">
                  <div className="gm-strong">{formatDate(tsToDate(d.created_at), t.locale, { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                  <div className="gm-tiny gm-muted">{t('programHistoryExerciseCountLabel', countExercises(p))}</div>
                </div>
                <button type="button" className="gm-btn gm-btn--soft gm-btn--sm" onClick={() => setView(d)}>{t('programHistoryShow')}</button>
                <button type="button" className="gm-icon-btn gm-icon-btn--sm" onClick={() => remove(d)} title={t('programHistoryDeleteTitle')}><Icon name="delete" outline /></button>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}

/* ───────── Program editor ───────── */
function ExerciseTile({ entry, exercise, durationFirst, readOnly, onAction, onPreview, dragProps, dragging, dropTarget }) {
  const t = useT();
  const hasDur = entry.durationMin > 0;
  const onlyDur = durationFirst && hasDur;
  const meta = [t.muscle(exercise?.muscle), t.equipment(exercise?.equipment)].filter(Boolean).join(' · ');
  return (
    <div className={`gm-ptile${dragging ? ' dragging' : ''}${dropTarget ? ' drop-target' : ''}`} {...dragProps}>
      {!readOnly && <span className="gm-ptile__handle" title={t('dragToReorder')}><Icon name="drag_indicator" /></span>}
      <ExerciseThumb exercise={exercise} large onClick={() => exercise && onPreview(exercise)} />
      <div
        className={`gm-grow${readOnly ? '' : ' gm-ptile__body'}`}
        onClick={readOnly ? undefined : () => onAction('edit')}
        role={readOnly ? undefined : 'button'}
        tabIndex={readOnly ? undefined : 0}
        onKeyDown={readOnly ? undefined : (e) => { if (e.key === 'Enter') onAction('edit'); }}
      >
        <div className="gm-ex-name">{exercise?.name || entry.exerciseId}</div>
        {meta && <div className="gm-ex-meta">{meta}</div>}
        <div className="gm-ptile__stats">
          {!onlyDur && <span className="gm-vchip">{entry.sets} {t('programSetsShort')} × {entry.reps} {t('programRepsShort')}</span>}
          {hasDur && <span className="gm-vchip">{entry.durationMin} {t('programExerciseDurationMinSuffix')}</span>}
          {!onlyDur && entry.weight != null && <span className="gm-vchip">{formatWeight(entry.weight)} kg</span>}
          {!onlyDur && entry.weight == null && !readOnly && <span className="gm-vchip gm-vchip--muted">+ kg</span>}
        </div>
      </div>
      {!readOnly && (
        <Menu
          trigger={({ toggle }) => (
            <button type="button" className="gm-icon-btn" onClick={toggle} title={t('programExerciseOptions')} aria-label={t('programExerciseOptions')}>
              <Icon name="more_vert" />
            </button>
          )}
        >
          <button type="button" onClick={() => onAction('edit')}><Icon name="tune" />{t('edit')}</button>
          <button type="button" onClick={() => onAction('replace')}><Icon name="swap_horiz" />{t('programReplaceExercise')}</button>
          <button type="button" onClick={() => onAction('copy')}><Icon name="content_copy" outline />{t('programCopyToDays')}</button>
          <hr />
          <button type="button" className="danger" onClick={() => onAction('delete')}><Icon name="delete" outline />{t('remove')}</button>
        </Menu>
      )}
    </div>
  );
}

/**
 * Program editor. `trainerMode` = a trainer editing an athlete's program: same screen,
 * but nothing that writes to the athlete's own user doc / subcollections (starter-todos,
 * AI insight, snapshots) — the trainer can only write the shared exercisePrograms doc.
 *
 * All seven weekdays are shown; days missing from the program are rest days and
 * become training days as soon as an exercise is added or copied to them.
 */
export function ProgramEditor({ programId, program: remote, user, readOnly = false, showFabs = true, trainerMode = false, title }) {
  const t = useT();
  const toast = useToast();
  const confirm = useConfirm();
  const { byId } = useExercises();
  const [draft, setDraft] = useState(remote);
  const [day, setDay] = useState(todayDayKey);
  const [saving, setSaving] = useState(0);
  const [justSaved, setJustSaved] = useState(false);
  const [modal, setModal] = useState(null);
  const [preview, setPreview] = useState(null);
  const [drag, setDrag] = useState({ from: null, over: null });
  const pending = useRef(0);
  const customizeMarked = useRef(false);
  // Latest draft for callbacks that run later (undo toast).
  const draftRef = useRef(draft);
  draftRef.current = draft;

  // Adopt remote updates unless a local save is in flight (mirrors _pendingSaveCount).
  useEffect(() => {
    if (pending.current === 0) setDraft(remote);
  }, [remote]);

  const persist = async (next) => {
    setDraft(next);
    draftRef.current = next;
    if (readOnly) return;
    pending.current += 1;
    setSaving((s) => s + 1);
    try {
      await saveProgram(programId, next);
      if (!customizeMarked.current && user && !trainerMode) {
        customizeMarked.current = true;
        markStarterTodoDone(user.id, 'starterTodoCustomizeProgram');
        markStarterTodoDone(user.id, 'starterTodoCreateProgram');
      }
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 1500);
    } catch (e) {
      toast.error(t('failedToUpdateProgram'));
    } finally {
      pending.current -= 1;
      setSaving((s) => s - 1);
    }
  };

  const mutate = (change) => {
    const next = clone(draftRef.current);
    change(next);
    persist(next);
  };

  if (!draft) return null;

  const listFor = (d) => draft[d] || [];
  const isRest = (d) => !Object.prototype.hasOwnProperty.call(draft, d);
  const trainingDays = sortDays(Object.keys(draft));
  const daysWithExercises = DAY_KEYS.filter((d) => listFor(d).length > 0);
  const entries = listFor(day);
  const todayKey = todayDayKey();
  const close = () => setModal(null);

  const addExercises = (target, exercises) => {
    mutate((next) => appendEntries(next, target, exercises.map(defaultEntryFor)));
  };

  const editEntry = (index, updated, applyAll) => {
    const id = entries[index]?.exerciseId;
    if (!id) return;
    const values = { sets: updated.sets, reps: updated.reps, durationMin: updated.durationMin, weight: updated.weight };
    mutate((next) => {
      Object.keys(next).forEach((d) => {
        if (d !== day && !applyAll) return;
        next[d] = next[d].map((e) => (e.exerciseId === id ? { ...e, ...values } : e));
      });
    });
  };

  const replaceEntry = (index, exercise) => {
    const oldId = entries[index]?.exerciseId;
    mutate((next) => {
      if (next[day]?.[index]?.exerciseId === oldId) next[day][index] = withExercise(next[day][index], exercise);
    });
  };

  const copyEntryToDays = (index, targets) => {
    const entry = entries[index];
    if (!entry) return;
    mutate((next) => targets.forEach((d) => appendEntries(next, d, [entry])));
    toast.success(t('programCopiedToDays', targets.length));
  };

  const removeEntry = (index) => {
    const removed = entries[index];
    if (!removed) return;
    const removedDay = day;
    mutate((next) => { next[removedDay].splice(index, 1); });
    toast.show(t('exerciseDeleted'), {
      duration: 5000,
      action: {
        label: t('undo'),
        onClick: () => mutate((next) => {
          const list = next[removedDay];
          // The day may have become a rest day meanwhile; nothing to put it back into.
          if (!list || list.some((e) => e.exerciseId === removed.exerciseId)) return;
          list.splice(Math.min(index, list.length), 0, removed);
        }),
      },
    });
  };

  const reorder = (from, to) => {
    if (from === to || from == null || to == null) return;
    mutate((next) => {
      const [item] = next[day].splice(from, 1);
      next[day].splice(to, 0, item);
    });
  };

  const copyDayFrom = (target, source) => {
    mutate((next) => appendEntries(next, target, next[source] || []));
  };

  const copyDayTo = (source, targets) => {
    const list = listFor(source);
    mutate((next) => targets.forEach((d) => appendEntries(next, d, list)));
    toast.success(t('programCopiedToDays', targets.length));
  };

  const moveDay = (source, target) => {
    mutate((next) => {
      next[target] = next[source] || [];
      delete next[source];
    });
    setDay(target);
  };

  const clearDay = async (d) => {
    const ok = await confirm({ title: t('programClearDay'), message: t('programClearDayConfirm', t(d)), confirmLabel: t('remove'), danger: true });
    if (ok) mutate((next) => { next[d] = []; });
  };

  const makeRestDay = async (d) => {
    if (trainingDays.length <= 1) {
      toast.error(t('cannotRemoveLastDay'));
      return;
    }
    if (listFor(d).length) {
      const ok = await confirm({ title: t('programMakeRestDay'), message: t('programMakeRestDayConfirm', t(d)), confirmLabel: t('remove'), danger: true });
      if (!ok) return;
    }
    mutate((next) => { delete next[d]; });
  };

  const openCopyFrom = (d) => {
    if (!daysWithExercises.some((x) => x !== d)) {
      toast.warning(t('programNothingToCopy'));
      return;
    }
    setModal({ type: 'copyFrom', day: d });
  };

  const openMove = (d) => {
    if (!DAY_KEYS.some(isRest)) {
      toast.warning(t('programNoFreeDay'));
      return;
    }
    setModal({ type: 'move', day: d });
  };

  // Day header summary: "5 exercises · ~45 min" + the most-trained muscle groups.
  const muscleCounts = {};
  entries.forEach((e) => {
    const m = resolveExercise(byId, e)?.muscle;
    if (m) muscleCounts[m] = (muscleCounts[m] || 0) + 1;
  });
  const topMuscles = Object.entries(muscleCounts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([m]) => t.muscle(m));
  const rest = isRest(day);
  // Custom exercises already in the program, offered again in the pickers (one per id).
  const customInProgram = Object.values(
    Object.fromEntries(Object.values(draft).flat().filter((e) => e.name && !byId[e.exerciseId]).map((e) => [e.exerciseId, customExerciseFor(e)])),
  );
  const canInsights = !readOnly && !trainerMode && !!user;

  return (
    <>
      <div className="gm-page-head">
        <h1 className="gm-page-title">{title || t.upper(t('weeklyProgramTitle'))}</h1>
        {saving > 0 && <Spinner />}
        {saving === 0 && justSaved && <Icon name="cloud_done" outline title={t('saved')} />}
        {canInsights && (
          <>
            <button type="button" className="gm-icon-btn gm-icon-btn--insight" onClick={() => setModal({ type: 'insight' })} disabled={!trainingDays.length} title={t('aiInsight')} aria-label={t('aiInsight')}>
              <Icon name="auto_awesome" />
            </button>
            <button type="button" className="gm-icon-btn" onClick={() => setModal({ type: 'snapshot' })} disabled={!trainingDays.length} title={t('programHistorySheetTitle')} aria-label={t('programHistorySheetTitle')}>
              <Icon name="history" />
            </button>
          </>
        )}
      </div>

      <div className="gm-weektabs" role="tablist">
        {DAY_KEYS.map((d) => {
          const isRestDay = isRest(d);
          return (
            <button
              key={d}
              type="button"
              role="tab"
              aria-selected={d === day}
              aria-label={`${t(d)}${d === todayKey ? `, ${t('programToday')}` : ''}`}
              className={`gm-weektab${d === day ? ' active' : ''}${d === todayKey ? ' today' : ''}${isRestDay ? ' rest' : ''}`}
              onClick={() => setDay(d)}
            >
              <span className="gm-weektab__name">{t.upper(t.dayShort(d))}</span>
              <span className="gm-weektab__count">{isRestDay ? '–' : listFor(d).length}</span>
              <span className="dot" />
            </button>
          );
        })}
      </div>

      <div className="gm-row-between" style={{ margin: '14px 0 12px' }}>
        <div>
          <div className="gm-strong" style={{ fontSize: 16 }}>
            {t(day)}{day === todayKey ? ` · ${t('programToday')}` : ''}
          </div>
          <div className="gm-tiny gm-muted" style={{ lineHeight: 1.5 }}>
            {rest ? t('programRestDay') : t('programDaySummary', entries.length, estimateDayMinutes(entries))}
            {!rest && topMuscles.length > 0 && <><br />{topMuscles.join(', ')}</>}
          </div>
        </div>
        {!readOnly && (
          <Menu
            trigger={({ toggle }) => (
              <button type="button" className="gm-icon-btn" onClick={toggle} title={t('programDayOptions')} aria-label={t('programDayOptions')}>
                <Icon name="more_horiz" />
              </button>
            )}
          >
            <div className="gm-menu__head gm-strong">{t(day)}</div>
            <button type="button" onClick={() => setModal({ type: 'add', day })}><Icon name="add" />{t('addExercise')}</button>
            <button type="button" onClick={() => openCopyFrom(day)}><Icon name="content_copy" outline />{t('programCopyFromDay')}</button>
            {entries.length > 0 && <button type="button" onClick={() => setModal({ type: 'copyTo', day })}><Icon name="copy_all" outline />{t('programCopyToDays')}</button>}
            {!rest && <button type="button" onClick={() => openMove(day)}><Icon name="drive_file_move" outline />{t('programMoveDay')}</button>}
            {entries.length > 0 && <button type="button" onClick={() => clearDay(day)}><Icon name="clear_all" />{t('programClearDay')}</button>}
            {!rest && trainingDays.length > 1 && (
              <>
                <hr />
                <button type="button" className="danger" onClick={() => makeRestDay(day)}><Icon name="self_improvement" />{t('programMakeRestDay')}</button>
              </>
            )}
          </Menu>
        )}
      </div>

      {entries.length === 0 ? (
        <div className="gm-card">
          <Empty icon={rest ? 'self_improvement' : 'fitness_center'}>
            <div className="gm-strong" style={{ color: 'var(--gm-text)' }}>{rest ? t('programRestDay') : t('noExercisesForDay')}</div>
            {!readOnly && (
              <>
                <div>{rest ? t('programRestDayHint') : t('programEmptyDayHint')}</div>
                <div className="gm-row gm-wrap" style={{ justifyContent: 'center', marginTop: 12 }}>
                  <button type="button" className="gm-btn" onClick={() => setModal({ type: 'add', day })}><Icon name="add" /> {t('addExercise')}</button>
                  {daysWithExercises.some((d) => d !== day) && (
                    <button type="button" className="gm-btn gm-btn--outline" onClick={() => openCopyFrom(day)}><Icon name="content_copy" outline /> {t('programCopyFromDay')}</button>
                  )}
                </div>
              </>
            )}
          </Empty>
        </div>
      ) : (
        <div className="gm-stack-sm">
          {entries.map((e, i) => (
            <ExerciseTile
              key={`${day}-${e.exerciseId}`}
              entry={e}
              exercise={resolveExercise(byId, e)}
              durationFirst={user ? user.programDurationInsteadOfSetsReps : true}
              readOnly={readOnly}
              onAction={(action) => {
                if (action === 'delete') removeEntry(i);
                else setModal({ type: action, index: i });
              }}
              onPreview={setPreview}
              dragging={drag.from === i}
              dropTarget={drag.over === i && drag.from !== i}
              dragProps={readOnly ? {} : {
                draggable: true,
                onDragStart: (ev) => { ev.dataTransfer.effectAllowed = 'move'; setDrag({ from: i, over: null }); },
                onDragOver: (ev) => { ev.preventDefault(); if (drag.over !== i) setDrag((s) => ({ ...s, over: i })); },
                onDrop: (ev) => { ev.preventDefault(); reorder(drag.from, i); setDrag({ from: null, over: null }); },
                onDragEnd: () => setDrag({ from: null, over: null }),
              }}
            />
          ))}
        </div>
      )}

      {!readOnly && showFabs && (
        <div className="gm-fabs">
          <button type="button" className="gm-fab gm-fab--extended" onClick={() => setModal({ type: 'add', day })} title={t('addExercise')}>
            <Icon name="add" /><span className="gm-fab__label">{t('addExercise')}</span>
          </button>
        </div>
      )}

      {modal?.type === 'edit' && entries[modal.index] && (
        <EditEntryModal
          entry={entries[modal.index]}
          exercise={resolveExercise(byId, entries[modal.index])}
          appearsOnOtherDays={Object.keys(draft).some((d) => d !== day && draft[d].some((x) => x.exerciseId === entries[modal.index].exerciseId))}
          applyToAllDefault={!!user?.syncExerciseAcrossDays}
          onClose={close}
          onSave={(u, applyAll) => { editEntry(modal.index, u, applyAll); close(); }}
        />
      )}
      {modal?.type === 'add' && (
        <AddExerciseModal
          title={`${t('addExerciseTitle')} · ${t(modal.day)}`}
          idsInDay={listFor(modal.day).map((e) => e.exerciseId)}
          idsInProgram={Object.values(draft).flat().map((e) => e.exerciseId)}
          extra={customInProgram}
          onClose={close}
          onPick={(picked) => { addExercises(modal.day, picked); close(); }}
        />
      )}
      {modal?.type === 'replace' && entries[modal.index] && (
        <AddExerciseModal
          title={t('programReplaceExercise')}
          idsInDay={entries.map((e) => e.exerciseId)}
          extra={customInProgram}
          multi={false}
          onClose={close}
          onPick={([picked]) => { replaceEntry(modal.index, picked); close(); }}
        />
      )}
      {modal?.type === 'copy' && entries[modal.index] && (
        <DayPickerModal
          title={t('programSelectTargetDays')}
          multi
          disabledReason={t('programAlreadyInDay')}
          selectable={DAY_KEYS.filter((d) => !listFor(d).some((x) => x.exerciseId === entries[modal.index].exerciseId))}
          onClose={close}
          onPick={(targets) => { copyEntryToDays(modal.index, targets); close(); }}
        />
      )}
      {modal?.type === 'copyFrom' && (
        <DayPickerModal
          title={t('programSelectSourceDay')}
          selectable={daysWithExercises.filter((d) => d !== modal.day)}
          onClose={close}
          onPick={([source]) => { copyDayFrom(modal.day, source); close(); }}
        />
      )}
      {modal?.type === 'copyTo' && (
        <DayPickerModal
          title={t('programSelectTargetDays')}
          multi
          selectable={DAY_KEYS.filter((d) => d !== modal.day)}
          onClose={close}
          onPick={(targets) => { copyDayTo(modal.day, targets); close(); }}
        />
      )}
      {modal?.type === 'move' && (
        <DayPickerModal
          title={t('programSelectMoveTarget')}
          disabledReason={t('dayAlreadyExists')}
          selectable={DAY_KEYS.filter(isRest)}
          onClose={close}
          onPick={([target]) => { moveDay(modal.day, target); close(); }}
        />
      )}
      {modal?.type === 'insight' && canInsights && <InsightModal user={user} program={draft} onClose={close} />}
      {modal?.type === 'snapshot' && canInsights && <SnapshotModal user={user} program={draft} onClose={close} />}
      <ImagePreview exercise={preview} onClose={() => setPreview(null)} />
    </>
  );
}

export default function ProgramPage() {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useSession();
  const { program, loading, exists } = useProgram(user.exerciseProgramID);
  const [creating, setCreating] = useState(false);

  const create = async () => {
    setCreating(true);
    try {
      const id = await createProgram();
      await updateUser(user.id, { exerciseProgramID: id });
      toast.success(t('programCreatedSuccessfully'));
    } catch (e) {
      toast.error(t('failedToCreateProgram'));
    } finally {
      setCreating(false);
    }
  };

  if (user.exerciseProgramID && loading) return <Loading />;

  if (!user.exerciseProgramID || !exists) {
    return (
      <div className="gm-content gm-content--narrow gm-enter">
        <div className="gm-page-head"><h1 className="gm-page-title">{t.upper(t('weeklyProgramTitle'))}</h1></div>
        <div className="gm-card gm-card--pad-lg">
          <Empty icon="event_note">
            <div className="gm-strong" style={{ color: 'var(--gm-text)' }}>{t('noProgramAssigned')}</div>
            <div>{t('createNewProgramToGetStarted')}</div>
            <div className="gm-row gm-wrap" style={{ justifyContent: 'center', marginTop: 10 }}>
              <button type="button" className="gm-btn" onClick={create} disabled={creating}>{creating ? <Spinner /> : <Icon name="add" />} {t('addProgram')}</button>
              <button type="button" className="gm-btn gm-btn--outline" onClick={() => navigate('/ai-trainer')}><Icon name="smart_toy" /> {t('aiTrainer')}</button>
            </div>
          </Empty>
        </div>
      </div>
    );
  }

  return (
    <div className="gm-content gm-content--narrow gm-enter" style={{ paddingBottom: 220 }}>
      <ProgramEditor programId={user.exerciseProgramID} program={program} user={user} />
    </div>
  );
}
